import type {
  DocumentUpsertModel,
  HomeListingLicensesModel,
  HomeListingLicensesUpdateModel,
} from 'api/models';
import {
  MediaFieldValue,
  parseDateMMDDYYYY,
  toDisplayDate,
  toIso,
  toMediaValue,
} from '../licenses.shared';

/**
 * Which lead certificate the home has. Derived from the generated envelope so the values stay checked
 * against the API rather than being restated as free-standing string literals.
 *
 * Unlike Michigan's program, this one is the homeowner's answer: Rhode Island accepts either
 * certificate, and `None` is a real answer rather than an unfilled field — it is what routes the home
 * to an inspection instead of to a lease.
 */
export type RICertificateType = NonNullable<
  NonNullable<HomeListingLicensesModel['ri']>['certificateType']
>;

export const RI_CERTIFICATE_TYPE = {
  CONFORMANCE: 'LeadConformance',
  LEAD_FREE: 'LeadFree',
  NONE: 'None',
} as const satisfies Record<string, RICertificateType>;

/** A Certificate of Lead Conformance stays valid this many years from its issue date. */
const LEAD_CONFORMANCE_VALIDITY_YEARS = 2;

/** The two-year expiry, or undefined for the Lead-Free certificate, which never expires. */
export function getExpirationDate(
  certificateType: unknown,
  issuanceDate?: string,
): Date | undefined {
  if (certificateType !== RI_CERTIFICATE_TYPE.CONFORMANCE) return undefined;

  const issued = parseDateMMDDYYYY(issuanceDate);
  if (!issued) return undefined;

  const expiry = new Date(issued);
  expiry.setFullYear(expiry.getFullYear() + LEAD_CONFORMANCE_VALIDITY_YEARS);
  return expiry;
}

export function isExpired(expiration?: Date): boolean {
  if (!expiration) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return expiration <= today;
}

export function formatExpirationDate(expiration: Date): string {
  return expiration.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Form state for the Rhode Island Licenses step. Field names mirror `RILicensesUpdateModel`, so the
 * mapping below only has to add the market envelope and convert the date and the uploaded file.
 *
 * Declared as a `type` rather than an `interface` so it keeps the implicit index signature that
 * `StepFormLayout`'s `initialValues: Record<string, unknown>` requires.
 */
export type RILicensesFormValues = {
  certificateType?: RICertificateType;
  certificateNumber?: string;
  issuanceDate?: string;
  file?: MediaFieldValue;
};

/** Maps `GET …/unit/licenses/{stepId}` onto form state. */
export function toRILicensesFormValues(
  currentStepFormData?: HomeListingLicensesModel | null,
): RILicensesFormValues {
  const ri = currentStepFormData?.ri;
  if (!ri) return {};

  const values: RILicensesFormValues = {
    certificateType: ri.certificateType,
    certificateNumber: ri.certificateNumber,
    issuanceDate: toDisplayDate(ri.issuanceDate),
  };

  const file = toMediaValue(ri.file);
  if (file) values.file = file;

  return values;
}

/**
 * Builds the `PUT …/unit/licenses/{stepId}` body. The market envelope is required:
 * `HomeListingLicensesUpdateModel` carries nothing but the per-market payloads, so a flat body is
 * discarded wholesale by the backend.
 *
 * The `HomeListingLicensesUpdateModel` return type is load-bearing — it is what keeps every field
 * name here checked against the generated client instead of failing silently at runtime.
 */
export function toRILicensesUpdatePayload(
  values: RILicensesFormValues,
  { file }: { file?: DocumentUpsertModel },
): HomeListingLicensesUpdateModel {
  // The year is confirmed in Disclosures and read from the property server-side, so it is not sent
  // from here. An exempt home has its certificate fields discarded by the backend either way.
  const ri: NonNullable<HomeListingLicensesUpdateModel['ri']> = {
    certificateType: values.certificateType,
  };

  if (values.certificateType !== RI_CERTIFICATE_TYPE.NONE) {
    ri.certificateNumber = values.certificateNumber;
    ri.issuanceDate = toIso(values.issuanceDate);
    ri.file = file;
  }

  return { ri };
}
